import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Popper,
  Paper,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Button,
  ClickAwayListener,
} from "@mui/material";
import { blue, green, grey, orange } from "@mui/material/colors";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import UploadDetailsDialog from "./UploadDetailsDialog";

const RoleDropdown = ({
  role,
  admins,
  assignedUser,
  onRoleChange,
  assignRoleLoading,
  assigningRoleId,
  onRemoveRole,
  deleteRolesLoading,
  linkedInUrl,
  setLinkedInUrl,
  resume,
  setResume,
  refetchResumes,
  refetchMetadata,
  metadataLoading,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const handleClick = (event) => {
    if (assignRoleLoading && assigningRoleId === role?.id) return;
    if (assignedUser && !event?.target?.closest(".remove-button")) return;
    setAnchorEl(event?.currentTarget);
    setSearchTerm("");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleSelectAdmin = (adminId) => {
    if (assignedUser) return;
    onRoleChange(role?.id, adminId);
    handleClose();
  };

  const filteredAdmins = admins?.filter(
    (admin) =>
      (admin?.in_scope !== false) &&
      (admin?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin?.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isAssigned = !!assignedUser;
  const isDisabled = assignRoleLoading && assigningRoleId === role?.id;
  const isIncomplete = isAssigned && !resume && !linkedInUrl;

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          border: "1px",
          borderStyle: isAssigned ? "solid" : "dashed",
          borderColor: isAssigned
            ? isIncomplete
              ? orange[300]
              : green[200]
            : grey[300],
          borderRadius: 1,
          cursor:
            isDisabled || (isAssigned && !open) ? "not-allowed" : "pointer",
          minWidth: 300,
          bgcolor: isAssigned
            ? isIncomplete
              ? orange[50]
              : green[50]
            : "background.paper",
          opacity: isDisabled ? 0.5 : 1,
          transition: "all 0.2s ease",
          "&:hover": !isDisabled &&
            !isAssigned && {
              borderColor: blue[200],
              bgcolor: blue[50],
            },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Box sx={{ color: isAssigned ? role?.color : grey[500] }}>
            {role?.icon}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: isAssigned ? "text.primary" : grey[700],
              }}
            >
              {role?.name}
            </Typography>
            {isAssigned ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "0.75rem",
                    bgcolor: blue[500],
                  }}
                >
                  {assignedUser?.name?.charAt(0)}
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  {assignedUser?.name}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: grey[600], fontWeight: 500 }}
              >
                Unassigned
              </Typography>
            )}
          </Box>
        </Box>

        <Button
          onClick={(e) => {
            e.stopPropagation();
            setUploadDialogOpen(true);
          }}
          sx={{
            visibility: isAssigned ? "visible" : "hidden",
            minWidth: 0,
            padding: 0.5,
            color: grey[600],
            "&:hover": { color: blue[700] },
            cursor: "pointer",
          }}
        >
          <FileUploadOutlinedIcon />
        </Button>

        <ExpandMoreIcon
          sx={{
            color: isAssigned ? "transparent" : grey[400],
            display: isAssigned ? "none" : "visible",
          }}
        />
      </Box>

      {uploadDialogOpen && (
        <UploadDetailsDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          resume={resume}
          setResume={setResume}
          linkedInUrl={linkedInUrl}
          setLinkedInUrl={setLinkedInUrl}
          assignedUserId={assignedUser?.id}
          assignedUserEmail={assignedUser?.email}
          refetchResumes={refetchResumes}
          refetchMetadata={refetchMetadata}
          metadataLoading={metadataLoading}
        />
      )}

      <Popper
        open={open && !isDisabled && !isAssigned}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          zIndex: 9999,
          width: anchorEl?.offsetWidth || "auto",
          maxWidth: "100%",
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={8} sx={{ mt: 1 }}>
            <Box
              sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <MenuList sx={{ maxHeight: 300, overflow: "auto" }}>
              {filteredAdmins?.map((admin) => (
                <MenuItem
                  key={admin?.id}
                  onClick={() => handleSelectAdmin(admin?.id)}
                  disabled={isAssigned}
                  sx={{
                    py: 1,
                    px: 2,
                    "&:hover": {
                      bgcolor: blue[50],
                    },
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: "0.75rem",
                        bgcolor: blue[500],
                      }}
                    >
                      {admin?.name?.charAt(0)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={admin?.name}
                    secondary={admin?.email}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </MenuItem>
              ))}
            </MenuList>

            {isAssigned && (
              <Box
                sx={{
                  p: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  fullWidth
                  size="small"
                  color="error"
                  className="remove-button"
                  disabled={deleteRolesLoading}
                  onClick={() => {
                    onRemoveRole(role?.id);
                    handleClose();
                  }}
                >
                  Remove Assignment
                </Button>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default RoleDropdown;
